import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { questionsApi } from '@/services/api/questions'
import type { CreateQuestionInput, UpdateQuestionInput } from '@/services/api/questions'

export function useQuestions() {
  return useQuery({
    queryKey: ['admin', 'questions'],
    queryFn: questionsApi.list,
    staleTime: 60_000,
  })
}

export function useQuestion(id: string) {
  return useQuery({
    queryKey: ['admin', 'questions', id],
    queryFn: () => questionsApi.get(id),
    enabled: !!id,
  })
}

export function useQuestionVotes(id: string) {
  return useQuery({
    queryKey: ['admin', 'questions', id, 'votes'],
    queryFn: () => questionsApi.getVotes(id),
    enabled: !!id,
  })
}

export function useQuestionResults(id: string) {
  return useQuery({
    queryKey: ['admin', 'questions', id, 'results'],
    queryFn: () => questionsApi.getResults(id),
    enabled: !!id,
  })
}

export function useCreateQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateQuestionInput) => questionsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions'] })
    },
  })
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateQuestionInput }) =>
      questionsApi.update(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions', variables.id] })
    },
  })
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => questionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions'] })
    },
  })
}

export function useChangeQuestionStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      questionsApi.changeStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'questions', variables.id] })
    },
  })
}
